import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { formatCurrency } from '@/utils/formatters';
import { distributeDiscount, maxDiscountOf } from '@/utils/discountAllocator';
import { tagNotes } from '@/utils/orderType';
import {
    calculateCart,
    clearCart,
    removeItem,
    setCustomerName,
    setCustomerPhone,
    setHeldCartCount,
    setNotes,
    setOrderType,
    setTotalDiscount,
    updateItemNotes,
    updateQuantity,
    type OrderType,
} from './cartSlice';

export interface HoldFields { customerName: string; customerPhone: string; notes: string }

/**
 * All cart intent in one place. Mount ONE controller with `calculate: true` per screen:
 * it owns the server re-calculation effect (POST cart/calculate on every change).
 * Totals, tax and max-discount always come from `calculation` — never summed client-side.
 */
export function useCartController({ calculate = true }: { calculate?: boolean } = {}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { selectedShop, shopConfig } = useAppSelector((s) => s.auth);
    const cart = useAppSelector((s) => s.cart);
    const { items, calculation, customerName, customerPhone, notes, totalDiscount, redeemPoints, calcNonce, orderType, isCalculating, error } = cart;

    const toRequestItems = useCallback(() => {
        const discounts = distributeDiscount(items, totalDiscount);
        return items.map((item, idx) => ({
            compositeId: item.compositeId,
            quantity: item.quantity,
            discount: discounts[idx],
            notes: item.notes,
            prescriptionRef: item.prescriptionRef,
            doctorName: item.doctorName,
            patientName: item.patientName,
            dosageInstructions: item.dosageInstructions,
        }));
    }, [items, totalDiscount]);

    useEffect(() => {
        if (!calculate || !selectedShop || items.length === 0) return;
        dispatch(calculateCart({
            shopId: selectedShop.shopId,
            request: { items: toRequestItems(), totalDiscount, customerName, customerPhone, notes, redeemPoints },
        }));
    }, [calculate, dispatch, selectedShop, items, totalDiscount, customerName, customerPhone, notes, redeemPoints, calcNonce, toRequestItems]);

    const setQty = useCallback((compositeId: number, quantity: number) => {
        if (Number.isFinite(quantity) && quantity > 0 && quantity <= 1000) dispatch(updateQuantity({ compositeId, quantity }));
    }, [dispatch]);

    const remove = useCallback((compositeId: number) => {
        dispatch(removeItem(compositeId));
        toast.success(t('cart.itemRemoved'));
    }, [dispatch, t]);

    const setItemNotes = useCallback((compositeId: number, value: string) => dispatch(updateItemNotes({ compositeId, notes: value })), [dispatch]);
    const changeOrderType = useCallback((type: OrderType) => dispatch(setOrderType(type)), [dispatch]);
    const changeNotes = useCallback((value: string) => dispatch(setNotes(value)), [dispatch]);
    const setCustomer = useCallback(({ name, phone }: { name: string; phone: string }) => {
        if (name !== customerName) dispatch(setCustomerName(name));
        if (phone !== customerPhone) dispatch(setCustomerPhone(phone));
    }, [dispatch, customerName, customerPhone]);

    const discountCap = Math.min(calculation?.maxDiscount ?? Infinity, maxDiscountOf(items));
    const setDiscount = useCallback((value: number) => {
        let discount = Math.max(0, value || 0);
        if (discount > discountCap) {
            discount = discountCap;
            toast.error(t('cart.discountExceedsMax', { amount: formatCurrency(discountCap) }));
        }
        dispatch(setTotalDiscount(discount));
    }, [dispatch, discountCap, t]);

    const clear = useCallback(() => {
        dispatch(clearCart());
        toast.success(t('cart.cartCleared'));
    }, [dispatch, t]);

    /** POST held cart, then empty the cart and go back to the catalog. Resolves true on success. */
    const hold = useCallback(async (fields: Partial<HoldFields> = {}) => {
        if (!selectedShop || items.length === 0) { toast.error(t('cart.empty')); return false; }
        try {
            const response = await api.post(API_ENDPOINTS.HOLD_CART(selectedShop.shopId), {
                items: toRequestItems(),
                customerName: fields.customerName || customerName,
                customerPhone: fields.customerPhone || customerPhone,
                // Order type rides in notes like checkout does, so Held carts can show and restore it
                notes: tagNotes(orderType, fields.notes || notes),
            });
            toast.success(t('heldCarts.cartHeld', { code: response.data.data.holdCode }));
            dispatch(clearCart());
            dispatch(setHeldCartCount(response.data.data.length));
            navigate('/catalog');
            return true;
        } catch (err) {
            toast.error(getErrorMessage(err));
            return false;
        }
    }, [selectedShop, items, toRequestItems, customerName, customerPhone, notes, orderType, dispatch, navigate, t]);

    const itemCount = useMemo(() => items.reduce((n, i) => n + i.quantity, 0), [items]);
    const canCheckout = items.length > 0 && !!calculation && !isCalculating;

    const checkout = useCallback(() => {
        if (items.length === 0) { toast.error(t('cart.empty')); return; }
        if (!calculation) { toast.error(t('cart.calculating')); return; }
        navigate('/checkout');
    }, [items.length, calculation, navigate, t]);

    return {
        shopId: selectedShop?.shopId ?? null,
        requireCustomerInfo: !!shopConfig?.requireCustomerInfo,
        items, calculation, isCalculating, error,
        customerName, customerPhone, notes, totalDiscount, orderType,
        itemCount, canCheckout, discountCap,
        setQty, remove, setItemNotes, setOrderType: changeOrderType, setNotes: changeNotes, setCustomer, setDiscount,
        hold, clear, checkout,
    };
}

export type CartController = ReturnType<typeof useCartController>;
